import { isDisliked } from "./eatingQuestions";
import { shortFoodName } from "./foodView";
import { totalsOf } from "./intake";
import {
  PLAN_MEAL_PROTEIN_G,
  daySkeleton,
  dowShort,
  entryOf,
  foodKey,
  fractionText,
  groupOf,
  logStats,
  outReason,
  roundPortion,
  usualPortion,
  weekdayWord,
  weeksLogged,
  type Candidate,
  type LogStat,
  type PlanTargets,
} from "./planner";
import type { FoodEntry, FoodGroup, FoodItem, KayaState, MealSlot } from "./types";

/**
 * Swapping a planned food: three like-for-like options with the reason for each, then any food in
 * the table with a verdict on whether it fits. (ADR-043; plan §3.5.6–7, the owner's note 3)
 *
 * Every figure is the meal and the day *as they would stand after the swap*: the planned food out,
 * the candidate in, nothing else touched. Nothing else on the day is trimmed to make room, and the
 * person's own items are never part of any arithmetic that moves them. Pure arithmetic over
 * `entryFor` values — no new store shape.
 */

const n = (v: number) => Math.round(v).toLocaleString();

/** The meal and the day one planned item sits in — everything a verdict is judged against. */
export interface SwapFrame {
  ds: string;
  meal: MealSlot;
  /** "lunch", "the morning" — the meal in a sentence */
  mealWord: string;
  /** the meal's share of the budget */
  share: number;
  old: FoodEntry;
  /** the whole day as planned, the old item included */
  day: FoodEntry[];
  T: PlanTargets;
  fast: boolean;
  /** each meal of the day in a sentence: "the snack", "the evening" */
  mealWords: Record<MealSlot, string>;
}

export function swapFrame(
  S: KayaState,
  ds: string,
  old: FoodEntry,
  day: FoodEntry[],
  T: PlanTargets,
  opts: { fast?: boolean; clock?: Date } = {}
): SwapFrame {
  const sk = daySkeleton(S, ds, T.kcal, { fast: opts.fast, today: opts.clock });
  const slot = sk.slots.find((s) => s.meal === old.meal);
  const label = slot?.label ?? old.meal;
  return {
    ds,
    meal: old.meal,
    mealWord: label.toLowerCase(),
    share: slot?.share ?? old.kcal,
    old,
    day,
    T,
    fast: !!opts.fast,
    mealWords: {
      breakfast: (sk.slots.find((s) => s.meal === "breakfast")?.label ?? "breakfast").toLowerCase(),
      lunch: (sk.slots.find((s) => s.meal === "lunch")?.label ?? "lunch").toLowerCase(),
      snack: (sk.slots.find((s) => s.meal === "snack")?.label ?? "snack").toLowerCase(),
      dinner: (sk.slots.find((s) => s.meal === "dinner")?.label ?? "dinner").toLowerCase(),
    },
  };
}

const mealOf = (f: SwapFrame, items: FoodEntry[]) => items.filter((e) => e.meal === f.meal);

/** The day with `cand` in place of the old item. */
function swapped(f: SwapFrame, cand: FoodEntry): FoodEntry[] {
  return f.day.map((e) => (e.id === f.old.id ? { ...cand, id: f.old.id, meal: f.meal } : e));
}

export interface Standing {
  mealKcal: number;
  mealProtein: number;
  dayKcal: number;
  dayProtein: number;
  daySodium: number;
}

export function standing(f: SwapFrame, cand: FoodEntry): Standing {
  const day = swapped(f, cand);
  const meal = totalsOf(mealOf(f, day));
  const t = totalsOf(day);
  return {
    mealKcal: meal.kcal,
    mealProtein: Math.round(meal.p),
    dayKcal: t.kcal,
    dayProtein: Math.round(t.p),
    daySodium: t.na,
  };
}

/* ---------- the three options ---------- */

const ROLE_WORD: Partial<Record<FoodGroup, string>> = {
  grain: "the cereal",
  legume: "the dal",
  veg: "the vegetable",
  leafy: "the greens",
  dairy: "the curd",
  drink: "the drink",
  fruit: "the fruit",
  nut: "the nuts",
  sweet: "the sweet",
  fried: "the snack",
  egg: "the egg",
};

const PROTEIN_GROUPS: FoodGroup[] = ["egg", "dairy", "legume", "meat", "fish"];

/** What the item does in its meal: the protein when it carries the meal's most, else its group. */
export function roleOf(f: SwapFrame, lookup: (code: string) => FoodItem | null): { word: string; groups: FoodGroup[] } {
  const meal = mealOf(f, f.day);
  const top = meal.reduce<FoodEntry | null>((a, e) => (!a || e.p > a.p ? e : a), null);
  const group = groupOf({ n: f.old.name, gr: f.old.code ? lookup(f.old.code)?.gr : undefined });
  if (top?.id === f.old.id && f.old.p >= 8) return { word: "the protein", groups: PROTEIN_GROUPS };
  if (!group) return { word: "this", groups: [] };
  return { word: ROLE_WORD[group] ?? "this", groups: [group] };
}

export interface SwapOption {
  entry: FoodEntry;
  /** "3 eggs" */
  portion: string;
  /** one clause on why, with the trade-off: "You log this most weeks · 6 g less protein" */
  reason: string;
}

export interface SwapOptions {
  /** "Same role: the protein of lunch · 240–400 kcal" */
  roleLine: string;
  options: SwapOption[];
}

/**
 * Three alternatives in the same role, each at the portion that matches the planned food's kcal
 * (rounded to its unit) and within ±25 % of it: the one with the most protein, the one the person
 * logs in most weeks, the one with the least sodium — each with the reason it is offered.
 */
export function swapOptions(
  S: KayaState,
  f: SwapFrame,
  cands: readonly Candidate[],
  lookup: (code: string) => FoodItem | null,
  today: string
): SwapOptions {
  const r = roleOf(f, lookup);
  const lo = Math.round(f.old.kcal * 0.75);
  const hi = Math.round(f.old.kcal * 1.25);
  const roleLine = `Same role: ${r.word} of ${f.mealWord} · ${n(lo)}–${n(hi)} kcal`;
  const onDay = new Set(f.day.map(foodKey));
  const dislikes = S.profile.eating?.dislikes ?? [];

  const pool: { c: Candidate; e: FoodEntry }[] = [];
  for (const c of cands) {
    if (!c.group || !r.groups.includes(c.group)) continue;
    if (onDay.has(c.key)) continue;
    if (outReason(S, c.item, { fast: f.fast })) continue;
    if (isDisliked({ i: c.item.i, n: c.item.n }, dislikes)) continue;
    const base = usualPortion(c, f.meal);
    const probe = entryOf(c, base, f.meal, "swap", 0);
    if (!probe || !(probe.kcal > 0)) continue;
    const qty = roundPortion((base.qty * f.old.kcal) / probe.kcal, base.unit);
    const e = entryOf(c, { ...base, qty }, f.meal, "swap", 0);
    if (!e || e.kcal < lo || e.kcal > hi) continue;
    pool.push({ c, e });
  }

  const T = f.T;
  const dayP = Math.round(totalsOf(f.day).p);
  const oldP = Math.round(f.old.p);
  const oldNa = f.old.na;
  const chosen: SwapOption[] = [];
  const taken = new Set<string>();
  const trade = (e: FoodEntry) => {
    const d = oldP - Math.round(e.p);
    return d >= 1 ? ` · ${d} g less protein` : "";
  };
  const add = (x: { c: Candidate; e: FoodEntry } | undefined, why: string) => {
    if (!x || taken.has(x.c.key)) return;
    taken.add(x.c.key);
    chosen.push({ entry: x.e, portion: fractionText(x.e.qty, x.e.unit), reason: why });
  };

  // 1 · the most protein, when it is more than the planned food's
  const richest = [...pool].sort((a, b) => b.e.p - a.e.p || b.c.key.localeCompare(a.c.key))[0];
  if (richest && Math.round(richest.e.p) > oldP) {
    // Summed as the rows show them: 118 − 18 + 22.
    const reach = dayP - oldP + Math.round(richest.e.p);
    const why =
      dayP < T.protein && reach >= T.protein
        ? `Closes the protein gap: ${dowShort(f.ds)} reaches ${reach} of ${T.protein} g`
        : `${Math.round(richest.e.p) - oldP} g more protein`;
    add(richest, why);
  }

  // 2 · the one the person eats most weeks
  const weekly = pool
    .filter((x) => !taken.has(x.c.key))
    .map((x) => ({ x, w: weeksLogged(x.c.stat, today) }))
    .filter((y) => y.w >= 2)
    .sort((a, b) => b.w - a.w || (b.x.c.stat?.n ?? 0) - (a.x.c.stat?.n ?? 0))[0];
  if (weekly) add(weekly.x, `${weekly.w >= 3 ? "You log this most weeks" : `You logged this in ${weekly.w} of the last 4 weeks`}${trade(weekly.x.e)}`);

  // 3 · less sodium
  if (typeof oldNa === "number") {
    const saltless = pool
      .filter((x) => !taken.has(x.c.key) && typeof x.e.na === "number" && (x.e.na ?? 0) < oldNa)
      .sort((a, b) => (a.e.na ?? 0) - (b.e.na ?? 0))[0];
    if (saltless) add(saltless, `Lower sodium: ${n(saltless.e.na ?? 0)} mg against ${n(oldNa)}${trade(saltless.e)}`);
  }

  // Fill to three with the closest in kcal.
  for (const x of [...pool].sort((a, b) => Math.abs(a.e.kcal - f.old.kcal) - Math.abs(b.e.kcal - f.old.kcal))) {
    if (chosen.length >= 3) break;
    add(x, `Close in kcal: ${n(x.e.kcal)} against ${n(f.old.kcal)}${trade(x.e)}`);
  }
  return { roleLine, options: chosen.slice(0, 3) };
}

/* ---------- the verdict (kit §35) ---------- */

export type VerdictKind = "fits" | "short" | "under" | "over" | "out";

export interface Verdict {
  kind: VerdictKind;
  /** the row's third line, which carries the verdict in words */
  text: string;
  item: FoodItem;
  /** null for an out row */
  entry: FoodEntry | null;
  /** "½ plate, your usual", "1 plate" */
  portion: string;
  usual: boolean;
  at: Standing | null;
  /** meal kcal past its share (over) or short of it (under) */
  by: number;
}

const inShare = (kcal: number, share: number) => kcal >= share * 0.9 && kcal <= share * 1.1;

/** The meal's floor: the plan's per-meal protein, and none for a snack. */
function floorOf(f: SwapFrame): number {
  return f.meal === "snack" ? 0 : f.T.mealFloor || PLAN_MEAL_PROTEIN_G;
}

/** Judges `e` in the old item's place. */
export function judge(f: SwapFrame, e: FoodEntry): Pick<Verdict, "kind" | "text" | "at" | "by"> {
  const at = standing(f, e);
  const floor = floorOf(f);
  const dayOver = at.dayKcal - f.T.kcal;
  const dayWord = weekdayWord(f.ds);
  const meal = f.mealWord;
  if (at.mealKcal > f.share * 1.1) {
    const by = at.mealKcal - f.share;
    const day = dayOver > 0 ? `${dayWord} ends ${n(dayOver)} over` : `${dayWord} stays within ~${n(f.T.kcal)}`;
    return { kind: "over", by, at, text: `Over by ${n(by)} kcal — ${meal} ${n(at.mealKcal)} of ~${n(f.share)}; ${day}` };
  }
  if (at.mealKcal < f.share * 0.9) {
    const by = f.share - at.mealKcal;
    return { kind: "under", by, at, text: `Under by ${n(by)} kcal — ${meal} ${n(at.mealKcal)} of ~${n(f.share)}` };
  }
  if (floor && at.mealProtein < floor) {
    return {
      kind: "short",
      by: floor - at.mealProtein,
      at,
      text: `Fits the kcal · protein short by ${floor - at.mealProtein} g (${meal} ${floor} g floor)`,
    };
  }
  if (at.daySodium > f.T.sodiumMax) {
    return {
      kind: "short",
      by: at.daySodium - f.T.sodiumMax,
      at,
      text: `Fits the kcal · sodium ${n(at.daySodium)} of ${n(f.T.sodiumMax)} mg for the day`,
    };
  }
  if (Math.abs(dayOver) > f.T.kcal * 0.1) {
    const way = dayOver > 0 ? "over" : "under";
    return { kind: "over", by: Math.abs(dayOver), at, text: `Fits ${meal}, but ${dayWord} ends ${n(Math.abs(dayOver))} ${way}` };
  }
  const protein = floor ? ` · protein ${at.mealProtein} g` : "";
  return { kind: "fits", by: 0, at, text: `Fits — ${meal} ${n(at.mealKcal)} of ~${n(f.share)}${protein}` };
}

/**
 * The verdict on one food for the old item's place: at the portion the person logs, when they have
 * logged it, else one serving. A food the rules keep out says why and is not judged.
 */
export function verdictFor(
  S: KayaState,
  f: SwapFrame,
  item: FoodItem,
  stats: Map<string, LogStat>
): Verdict {
  const key = item.i || `name:${item.n.toLowerCase()}`;
  const stat = stats.get(key);
  const c: Candidate = { key, item, group: groupOf(item), stat };
  const why = outReason(S, item, { fast: f.fast });
  const p = usualPortion(c, f.meal);
  const portion = `${fractionText(p.qty, p.unit)}${p.usual ? ", your usual" : ""}`;
  const e = entryOf(c, p, f.meal, `swap:${key}`, 0);
  // A ruled-out row still shows what it is — its portion, kcal and protein — muted, never judged.
  if (why) return { kind: "out", text: `Out — ${why}`, item, entry: e, portion, usual: p.usual, at: null, by: 0 };
  if (!e) return { kind: "out", text: "Out — it has no portion to plan", item, entry: null, portion, usual: p.usual, at: null, by: 0 };
  return { ...judge(f, e), item, entry: e, portion, usual: p.usual };
}

const RANK: Record<VerdictKind, number> = { fits: 0, short: 1, under: 2, over: 3, out: 4 };

/** Sorted by fit: fits (closest to the share first), short, under, over (least first), out last. */
export function sortVerdicts(f: SwapFrame, vs: Verdict[]): Verdict[] {
  return [...vs].sort((a, b) => {
    if (RANK[a.kind] !== RANK[b.kind]) return RANK[a.kind] - RANK[b.kind];
    if (a.kind === "fits") return Math.abs((a.at?.mealKcal ?? 0) - f.share) - Math.abs((b.at?.mealKcal ?? 0) - f.share);
    return a.by - b.by;
  });
}

export interface SearchView {
  shown: Verdict[];
  /** over rows past the first two, folded under one row */
  folded: Verdict[];
  /** "7 more pulao dishes" */
  foldLabel: string | null;
  /** "All over lunch at one serving" */
  foldSub: string | null;
  out: Verdict[];
}

/** What the search sheet shows: every row that fits or nearly does, the over row closest to
 *  fitting and the over row with the most protein, the rest of the over rows folded, and the
 *  ruled-out ones last. */
export function searchView(f: SwapFrame, vs: Verdict[], query: string): SearchView {
  const sorted = sortVerdicts(f, vs);
  const live = sorted.filter((v) => v.kind !== "out");
  const over = live.filter((v) => v.kind === "over");
  // Of the rows that do not fit, two stay open, each the best at something: the one closest to
  // fitting, and the one that brings the most protein — the two a person would weigh.
  const richest = [...over].sort((a, b) => (b.entry?.p ?? 0) - (a.entry?.p ?? 0) || a.by - b.by)[0];
  const shownOver = over.filter((v, i) => i === 0 || v === richest);
  const folded = over.filter((v) => !shownOver.includes(v));
  const shown = live.filter((v) => v.kind !== "over" || shownOver.includes(v));
  const q = query.trim().toLowerCase();
  const word = folded.length === 1 ? "dish" : "dishes";
  return {
    shown,
    folded,
    foldLabel: folded.length ? `${folded.length} more ${q ? `${q} ` : ""}${word}` : null,
    foldSub: folded.length ? `All over ${f.mealWord}${folded.every((v) => !v.usual) ? " at one serving" : ""}` : null,
    out: sorted.filter((v) => v.kind === "out"),
  };
}

/* ---------- the fit panel ---------- */

export type LineTone = "danger" | "caution" | "ink";

export interface FitLine {
  label: string;
  tone: LineTone;
  text: string;
}

export interface FitPanel {
  /** "Doesn't fit as it is" */
  verdict: string;
  tone: LineTone;
  /** "One plate is 184 kcal more than lunch holds; ¾ plate fits, at lunch 686 and protein 30 g." */
  sentence: string;
  lines: FitLine[];
  /** the largest household portion that fits, tried as ¾, ½, ¼ of the one shown */
  best: { entry: FoodEntry; label: string } | null;
  /** "Swap anyway — the day ends 184 over" */
  anyway: string;
  /** "Keep paneer bhurji" */
  keep: string;
  /** "Nothing else on the day changes; you placed the snack, so it is never trimmed." */
  note: string;
}

const PORTIONS = [0.75, 0.5, 0.25];

/** "One plate", "½ plate", "2 eggs" at the start of a sentence. */
function portionLead(e: FoodEntry): string {
  if (e.qty === 1 && !/^\d/.test(e.unit)) return `One ${e.unit}`;
  return fractionText(e.qty, e.unit);
}

export function fitPanel(f: SwapFrame, v: Verdict, lookup: (code: string) => FoodItem | null): FitPanel | null {
  const e = v.entry;
  if (!e || !v.at) return null;
  const at = v.at;
  const floor = floorOf(f);
  const meal = f.mealWord;
  const dayWord = weekdayWord(f.ds);
  const dayOver = at.dayKcal - f.T.kcal;
  const before = standing(f, f.old);

  // The largest smaller portion that fits.
  let best: FitPanel["best"] = null;
  const row = e.code ? lookup(e.code) : null;
  const c: Candidate = { key: foodKey(e), item: row ?? v.item, group: groupOf(v.item) };
  for (const k of PORTIONS) {
    const qty = e.qty * k;
    const x = entryOf(c, { qty, unit: e.unit, oilTsp: e.oilTsp }, f.meal, e.id, e.at);
    if (!x) continue;
    if (judge(f, x).kind === "fits") {
      best = { entry: x, label: `${fractionText(qty, e.unit)} instead — ${n(x.kcal)} kcal: fits` };
      break;
    }
  }

  const lines: FitLine[] = [];
  const mealCap = meal.charAt(0).toUpperCase() + meal.slice(1);
  if (at.mealKcal > f.share * 1.1)
    lines.push({ label: `Kcal over · ${meal}`, tone: "danger", text: `${mealCap} would be ${n(at.mealKcal)} of ~${n(f.share)} kcal.` });
  else if (at.mealKcal < f.share * 0.9)
    lines.push({ label: `Kcal under · ${meal}`, tone: "caution", text: `${mealCap} would be ${n(at.mealKcal)} of ~${n(f.share)} kcal.` });
  else lines.push({ label: `Kcal · fine`, tone: "ink", text: `${mealCap} would be ${n(at.mealKcal)} of ~${n(f.share)} kcal.` });

  if (dayOver > 0 && at.mealKcal > f.share * 1.1) {
    const left = f.T.deficit - dayOver;
    const deficit =
      left > 0
        ? `the day’s deficit drops from ~${n(f.T.deficit)} to ~${n(left)}`
        : `the day’s deficit of ~${n(f.T.deficit)} is gone`;
    lines.push({
      label: "Kcal over · the day",
      tone: "danger",
      text: `${dayWord} would end ${n(at.dayKcal)} of ~${n(f.T.kcal)} — ${n(dayOver)} over; ${deficit}.`,
    });
  } else {
    lines.push({ label: "Kcal · the day, fine", tone: "ink", text: `${dayWord} would end ${n(at.dayKcal)} of ~${n(f.T.kcal)}.` });
  }

  const dp = at.dayProtein - before.dayProtein;
  const same = dp === 0 ? "the same as planned" : dp > 0 ? `${dp} g more than planned` : `${-dp} g less than planned`;
  if (floor && at.mealProtein < floor)
    lines.push({
      label: "Protein · short",
      tone: "caution",
      text: `${mealCap} would have ${at.mealProtein} of its ${floor} g; the day ${at.dayProtein} of ${f.T.protein} g.`,
    });
  else lines.push({ label: "Protein · fine", tone: "ink", text: `Protein ${at.dayProtein} of ${f.T.protein} g — ${same}.` });

  if (at.daySodium > f.T.sodiumMax)
    lines.push({
      label: "Sodium · over",
      tone: "danger",
      text: `Sodium ${n(at.daySodium)} of ${n(f.T.sodiumMax)} mg — ${n(at.daySodium - f.T.sodiumMax)} over.`,
    });
  else lines.push({ label: "Sodium · fine", tone: "ink", text: `Sodium ${n(at.daySodium)} of ${n(f.T.sodiumMax)} mg.` });

  const lead = portionLead(e);
  let why: string;
  if (v.kind === "over") why = `${lead} is ${n(at.mealKcal - f.share)} kcal more than ${meal} holds`;
  else if (v.kind === "under") why = `${lead} leaves ${meal} ${n(f.share - at.mealKcal)} kcal short`;
  else if (v.kind === "short" && floor && at.mealProtein < floor) why = `${lead} leaves ${meal} ${floor - at.mealProtein} g short of protein`;
  else why = `${lead} takes the day past its sodium ceiling`;
  let sentence = why;
  if (best) {
    const b = standing(f, best.entry);
    sentence += `; ${fractionText(best.entry.qty, e.unit)} fits, at ${meal} ${n(b.mealKcal)} and protein ${b.mealProtein} g.`;
  } else sentence += "; no smaller portion fits either.";

  const anyway =
    dayOver > 0
      ? `Swap anyway — the day ends ${n(dayOver)} over`
      : v.kind === "short" && floor && at.mealProtein < floor
        ? `Swap anyway — ${meal} ends ${floor - at.mealProtein} g short`
        : "Swap anyway";

  const own = f.day.filter((x) => !x.id.startsWith("g:") && x.id !== f.old.id);
  const ownMeals = [...new Set(own.map((x) => f.mealWords[x.meal]))];
  const note = ownMeals.length
    ? `Nothing else on the day changes; you placed the ${ownMeals.join(" and the ")}, so ${ownMeals.length === 1 ? "it is" : "they are"} never trimmed.`
    : "Nothing else on the day changes.";

  return {
    verdict: v.kind === "fits" ? "Fits" : "Doesn’t fit as it is",
    tone: v.kind === "over" ? "danger" : v.kind === "fits" ? "ink" : "caution",
    sentence,
    lines,
    best,
    anyway,
    keep: `Keep ${shortFoodName(f.old.name).toLowerCase()}`,
    note,
  };
}

/** The candidate stats a search needs, built once per sheet. */
export function statsFor(S: KayaState): Map<string, LogStat> {
  return logStats(S);
}
